import React, { useState, useEffect, useRef } from 'react'
import { 
  Plus,
  Download,
  ArrowLeft,
  ChevronRight,
  Home,
  DollarSign,
  Users,
  Package,
  TrendingUp
} from 'lucide-react'
import DataTable from '../../components/DataTable/DataTable'
import Breadcrumb from '../../components/Common/Breadcrumb'
import ConfirmationModal from '../../components/ConfirmationModal/ConfirmationModal'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import serviceMasterService from '../../services/serviceMasterService'
import '../../components/ConfirmationModal/ConfirmationModal.css'
// Consolidated CSS is imported globally

const ServiceMaster = () => {
  const navigate = useNavigate()
  const [selectedRows, setSelectedRows] = useState([])
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(true)
  const [stats, setStats] = useState({
    totalServices: 0,
    activeServices: 0,
    inactiveServices: 0
  })
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [deleteItem, setDeleteItem] = useState(null)
  const hasLoadedRef = useRef(false)
  const hasShownEmptyToastRef = useRef(false)

  // Load data from backend
  useEffect(() => {
    if (!hasLoadedRef.current) {
      hasLoadedRef.current = true
      loadData()
    }
  }, [])

  const loadData = async () => {
    try {
      setLoading(true)
      const [servicesResponse, statsResponse] = await Promise.all([
        serviceMasterService.getAllServices(),
        serviceMasterService.getServiceStats()
      ])
      
      if (servicesResponse.success) {
        // Transform _id to id for frontend compatibility
        const transformedData = (servicesResponse.data || []).map(service => ({
          ...service,
          id: service._id
        }))
        setData(transformedData)
        // Only show toast if data is empty and we haven't shown it before
        if (servicesResponse.data?.length === 0 && !hasShownEmptyToastRef.current) {
          hasShownEmptyToastRef.current = true
          toast.info('No service master records found. Create your first service!')
        }
      } else {
        toast.error(servicesResponse.message || 'Failed to load services')
      }
      
      if (statsResponse.success) {
        setStats(statsResponse.data)
      } else {
        toast.error(statsResponse.message || 'Failed to load statistics')
      }
    } catch (error) {
      console.error('Error loading service master records:', error)
      if (error.response?.status === 401) {
        toast.error('Authentication required. Please log in again.')
      } else if (error.response?.status === 403) {
        toast.error('You do not have permission to view services')
      } else {
        toast.error('Failed to load service master records')
      }
    } finally {
      setLoading(false)
    }
  }

  const columns = [
    {
      accessorKey: 'serviceCode',
      header: 'Code',
      cell: ({ getValue }) => (
        <div className="service-code-cell" style={{ textAlign: 'left' }}>
          <span className="code-text">{getValue()}</span>
        </div>
      ),
    },
    {
      accessorKey: 'serviceDescription',
      header: 'Description',
      size: 300,
      cell: ({ getValue }) => (
        <div className="service-description-cell" style={{ textAlign: 'left', whiteSpace: 'normal', wordWrap: 'break-word' }}>
          <span className="description-text">{getValue() || '-'}</span>
        </div>
      ),
    },
    {
      accessorKey: 'selectSAC',
      header: 'SAC Code',
      cell: ({ getValue }) => (
        <div className="sac-cell" style={{ textAlign: 'center' }}>
          <span className="sac-text">{getValue() || '-'}</span>
        </div>
      ),
    },
    {
      accessorKey: 'gstRate',
      header: 'GST %',
      cell: ({ getValue }) => (
        <div className="gst-rate-cell" style={{ textAlign: 'center' }}>
          <span className="rate-text">{getValue()}%</span>
        </div>
      ),
    },
    {
      accessorKey: 'igstRate',
      header: 'IGST %',
      cell: ({ getValue }) => (
        <div className="igst-rate-cell" style={{ textAlign: 'center' }}>
          <span className="rate-text">{getValue()}%</span>
        </div>
      ),
    },
    {
      accessorKey: 'cgstRate',
      header: 'CGST %',
      cell: ({ getValue }) => (
        <div className="cgst-rate-cell" style={{ textAlign: 'center' }}>
          <span className="rate-text">{getValue()}%</span>
        </div>
      ),
    },
    {
      accessorKey: 'sgstRate',
      header: 'SGST %',
      cell: ({ getValue }) => (
        <div className="sgst-rate-cell" style={{ textAlign: 'center' }}>
          <span className="rate-text">{getValue()}%</span>
        </div>
      ),
    },
  ]

  const handleAdd = () => {
    navigate('/sales/service-master/form')
  }

  const handleEdit = (serviceData) => {
    navigate('/sales/service-master/edit', { state: { serviceData } })
  }

  const handleView = (serviceData) => {
    navigate('/sales/service-master/view', { state: { serviceData } })
  }

  const handleDelete = (serviceData) => {
    setDeleteItem(serviceData)
    setShowDeleteModal(true)
  }

  const confirmDelete = async () => {
    if (!deleteItem) return

    try {
      toast.info('Deleting service...')
      // Use _id instead of id
      const response = await serviceMasterService.deleteService(deleteItem._id)
      if (response.success) {
        toast.success('Service deleted successfully!', { autoClose: 2000 })
        loadData() // Reload data
      } else {
        toast.error(response.message || 'Failed to delete service')
      }
    } catch (error) {
      console.error('Error deleting service:', error)
      if (error.response?.status === 404) {
        toast.error('Service not found')
      } else if (error.response?.status === 403) {
        toast.error('You do not have permission to delete this service')
      } else {
        toast.error('Failed to delete service. Please try again.')
      }
    } finally {
      setShowDeleteModal(false)
      setDeleteItem(null)
    }
  }

  const cancelDelete = () => {
    setShowDeleteModal(false)
    setDeleteItem(null)
  }

  const handleExport = () => {
    // TODO: Implement export functionality
    toast.info('Export functionality coming soon!')
  }

  const breadcrumbItems = [
    {
      label: 'Sales',
      path: '/sales',
      onClick: () => navigate('/sales')
    },
    {
      label: 'Service Master',
      path: '/sales/service-master',
      isCurrent: true
    }
  ]

  return (
    <div className="sales-dashboard">
      {/* Breadcrumb Navigation */}
      <div className="breadcrumb-section">
        <Breadcrumb items={breadcrumbItems} />
      </div>

      {/* Page Header */}
      <div className="page-header">
        <div className="page-title">
          <h1>Service Master</h1>
          <p>Manage service codes, rates, and billing information</p>
        </div>
        <div className="page-actions">
          <button className="btn btn-primary" onClick={handleAdd}>
            <Plus size={16} />
            Add Service
          </button>
        </div>
      </div>

      {/* Metrics Section */}
      <div className="dashboard-metrics-grid">
        <div className="metric-card revenue">
          <div className="metric-header">
            <div className="metric-icon">
              <span>{stats.totalServices}</span>
            </div>
            <div className="metric-trend positive">
              <span>Total</span>
            </div>
          </div>
          <div className="metric-value">{stats.totalServices}</div>
          <div className="metric-title">Total Services</div>
          <div className="metric-subtitle">All services</div>
        </div>

        <div className="metric-card customers">
          <div className="metric-header">
            <div className="metric-icon">
              <span>{stats.activeServices}</span>
            </div>
            <div className="metric-trend positive">
              <span>Active</span>
            </div>
          </div>
          <div className="metric-value">{stats.activeServices}</div>
          <div className="metric-title">Active Services</div>
          <div className="metric-subtitle">Currently active</div>
        </div>

        <div className="metric-card churn">
          <div className="metric-header">
            <div className="metric-icon">
              <span>{stats.inactiveServices}</span>
            </div>
            <div className="metric-trend negative">
              <span>Inactive</span>
            </div>
          </div>
          <div className="metric-value">{stats.inactiveServices}</div>
          <div className="metric-title">Inactive Services</div>
          <div className="metric-subtitle">Currently inactive</div>
        </div>
      </div>

      {/* Data Table */}
      <div className="data-table-section">
        <DataTable
          data={data}
          columns={columns}
          loading={loading}
          onView={handleView}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onExport={handleExport}
          noCard={true}
          className="sac-master-table"
          pageSize={10}
        />
      </div>

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={cancelDelete}
        onConfirm={confirmDelete}
        title="Delete Service"
        message={`Are you sure you want to delete "${deleteItem?.serviceDescription}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />
    </div>
  )
}

export default ServiceMaster